// @ts-ignore
import { getRoutePathById } from '@bingads-webui-campaign-react/with-site-map';
// @ts-ignore
import URI from 'urijs';
import queryString from 'query-string';
import { History, Location } from 'history';

import { SmartCampaignPathPrefix, shouldNavigateCrossSite, isSmartCampaignPath, resetCampaignFiltersAndRedirect } from '../utils';
import { IRoute } from '../types/route.types';
import { IContext } from '../types/context.types';

export class Route implements IRoute {
  context: IContext;
  constructor(context: IContext) {
    this.context = context;
  }

  public getImportScheduleWithImportTypePath(importType) {
    const path = this.getPathById('import-schedule');
    const search = this.getImportScheduleWithImportTypeSearch(importType);
    const targetUrl = `${path}${search}`;

    return targetUrl;
  }

  public goImportSchedule() {
    const path = this.getPathById('import-schedule');
    this.goPath(path);
  }

  public goImportScheduleWithImportType(importType) {
    const targetUrl = this.getImportScheduleWithImportTypePath(importType);
    this.navigateByUrl(targetUrl);
  }

  public goGoogleImport() {
    const path = this.getPathById('import-google');
    this.goPath(path);
  }

  public getMetaImportWithImportSrcPath(importsrc) {
    const path = this.getPathById('import-facebook');
    const search = this.getMetaImportWithImportSrcSearch(importsrc);
    const targetUrl = `${path}${search}`;

    return targetUrl;
  }

  public goMetaimportWithImportSrc(importsrc) {
    const targetUrl = this.getMetaImportWithImportSrcPath(importsrc);
    this.navigateByUrl(targetUrl);
  }

  public goMetaImport() {
    const path = this.getPathById('import-facebook');
    this.goPath(path);
  }

  public goDisapprovedAds() {
    const path = this.getPathById('ads');
    const search = this.getDisapprovedSearch('filterDisapprovedAds');
    this.goPath(path, search);
  }

  public goDisapprovedAdResource() {
    const path = this.getPathById('ads');
    const search = this.getDisapprovedSearch('filterAdResourceInvalidAds');
    this.goPath(path, search);
  }

  public goDisapprovedKeywords() {
    const path = this.getPathById('keywords');
    const search = this.getDisapprovedSearch('filterDisapprovedKeywords');
    this.goPath(path, search);
  }

  public goMerchantCenterDraftStores() {
    const path = this.getPathById('mmc_placeholder_stores');

    const location: Location = this.context.getRouteLocation() as Location;
    const { values: { uid, cid, aid } } = this.context.getCurrentScope();

    // redirect to merchant center draft stores page if it is not the current page
    if (!location.pathname.match(path)) {
      // only include uid cid aid params since there might be other params in url which would make the merchant center page crash
      this.goPath(path, queryString.stringify({ uid, cid, aid }));
    }
  }

  public goMerchantCenterGMCImortPage(storeId) {
    const path = this.getPathById('mmc-import');
    const { values: { uid, cid, aid } } = this.context.getCurrentScope();
    const search = queryString.stringify({
      uid, cid, aid, storeId,
    });

    this.goPath(path, search);
  }

  public goTargetPage(siteMapId: string, search: string, fallbackHref: string) {
    const path = this.getPathById(siteMapId);
    if (path) {
      this.goPath(path, search);
    } else if (fallbackHref) {
      // the target page might not be in site map of current scope, so we need to fallback
      this.navigateByUrl(fallbackHref);
    }
  }

  // task 2687857: If we can get the path from site map, we could get rid of the hardcoded link
  public goAllSmartCampaigns() {
    // here we need to hack when we go to allSmart from smart itself. as smart is now using
    // a hash router, we cannot use the history push from global menu's none-hash router
    const location: Location = this.context.getRouteLocation() as Location;
    if (isSmartCampaignPath(location.pathname)) {
      window.location.hash = '#/';
    }
    this.goPath(SmartCampaignPathPrefix, '');
  }

  // go to all campaigns page
  // if any filter, could add related logic and use
  public async goAllCampaigns(campaignFilterType ?: string) {
    const path = this.getPathById('campaigns');
    const { values: { uid, cid, aid } } = this.context.getCurrentScope();
    const search = queryString.stringify({
      uid, cid, aid,
    });
    const targetUrl = `${path}?${search}`;
    const preferenceService = this.context.getPreferenceService();

    // reset related filters, then redirect to all campaigns page with correct filters
    await resetCampaignFiltersAndRedirect({
      preferenceService,
      campaignFilterType,
      callback: () => {
        this.navigateByUrl(targetUrl);
      },
    });
  }

  private getDisapprovedSearch(alias: string) {
    const disapproveStr = `filterAlias=${alias}`;
    const location: Location = this.context.getRouteLocation() as Location;
    const search = location && location.search ? `${location.search}&${disapproveStr}` : `?${disapproveStr}`;

    return search;
  }

  private getImportScheduleWithImportTypeSearch(importType: string) {
    const importTypeStr = `importType=${importType}`;
    const location: Location = this.context.getRouteLocation() as Location;

    // remove importType param if any
    location.search = location.search.replace(/&importType=.*&*/, '');

    const search = location && location.search ? `${location.search}&${importTypeStr}` : `?${importTypeStr}`;

    return search;
  }

  private getMetaImportWithImportSrcSearch(importsrc: string) {
    const location: Location = this.context.getRouteLocation() as Location;

    const urlSearchParams: URLSearchParams = new URLSearchParams(location.search);
    // set importsrc param if any, add importsrc if not exist
    urlSearchParams.set('importsrc', importsrc);

    return `?${urlSearchParams.toString()}`;
  }

  public getPathById(id: string) {
    const siteMap = this.context.getSiteMap();
    return getRoutePathById(siteMap, id);
  }

  public goPath(path: string, searchOri?: string) {
    const history: History = this.context.getRouteHistory() as History;
    const location: Location = this.context.getRouteLocation() as Location;
    const search = searchOri || location.search;

    if (shouldNavigateCrossSite(path, location.pathname) && new URI(path).is('relative')) {
      // eslint-disable-next-line no-param-reassign
      path = new URI(window.location.origin).segment(path).toString();
    }

    history.push({
      pathname: path,
      search,
    });
  }

  public navigateByUrl(href: string) {
    window.location.href = href;
  }
}
